import { useLocation } from "react-router-dom";
import {
  PAGE_META,
  PAGE_JSON_LD,
  SITE_URL,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_ALT,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  normalizeRoute,
  validatePageMeta,
} from "../seo/pageMeta";

/**
 * Per-route document metadata.
 * ---------------------------------------------------------------------------
 * Relies on React 19's native metadata hoisting: <title>, <meta>, and <link>
 * rendered anywhere in the tree are moved into <head> automatically, and
 * removed again when the component unmounts. That is why this needs no
 * react-helmet dependency (which has no React 19 support anyway).
 *
 * WHAT SEES WHAT — this matters, do not "simplify" it away:
 *   - Googlebot executes JavaScript, so it sees these per-route tags.
 *   - Social scrapers (Facebook, X, Discord, Slack, LinkedIn) and AI crawlers
 *     (GPTBot, ClaudeBot, PerplexityBot) do NOT execute JavaScript. They only
 *     ever see the brand-level fallback tags in public/index.html. Shared links
 *     therefore render the same generic card regardless of route. Fixing that
 *     requires prerendering the build to static HTML per route.
 *
 * The matching fallback tags in index.html are stripped before React renders
 * (see stripDefaultMeta in src/index.js), so there is never a duplicate
 * <title> — browsers and document.title use the FIRST title in the document.
 *
 * Usage: drop <Seo /> at the top of a page component. It reads the current
 * route from react-router and looks the metadata up in PAGE_META. Pass props
 * only to override.
 */

let hasValidated = false;

export default function Seo({
  path,
  title,
  description,
  image,
  imageAlt,
  type = "website",
  noindex,
  children,
}) {
  const location = useLocation();
  // Normalised so /About, /about/ and /about all resolve to one entry and one
  // canonical. react-router renders all three; nothing redirects between them.
  const routePath = normalizeRoute(path ?? location.pathname);

  // Dev-only stand-in for the build-time SEO validation in the spec (10.1).
  if (process.env.NODE_ENV !== "production" && !hasValidated) {
    hasValidated = true;
    const problems = validatePageMeta();
    if (problems.length > 0) {
      console.warn(
        "[Seo] PAGE_META validation problems:\n  - " + problems.join("\n  - ")
      );
    }
  }

  const isKnownRoute = Object.prototype.hasOwnProperty.call(
    PAGE_META,
    routePath
  );
  const entry = PAGE_META[routePath] ?? {};

  // Always fall back to brand-level values. index.html's tags are stripped
  // before render, so if this resolves to nothing the tab title goes blank and
  // useAnalytics reports an empty page_title.
  const resolvedTitle = title ?? entry.title ?? DEFAULT_TITLE;
  const resolvedDescription =
    description ?? entry.description ?? DEFAULT_DESCRIPTION;
  // Fail closed: an unrecognised path has no route in src/App.js, so it renders
  // blank. Never let a blank page be indexable.
  const resolvedNoindex = noindex ?? entry.noindex ?? !isKnownRoute;
  const resolvedImage = image ?? entry.image ?? DEFAULT_OG_IMAGE;
  const resolvedImageAlt =
    imageAlt ?? entry.imageAlt ?? DEFAULT_OG_IMAGE_ALT;

  if (process.env.NODE_ENV !== "production" && !isKnownRoute && !title) {
    console.warn(
      `[Seo] No PAGE_META entry for "${routePath}". Falling back to brand ` +
        `metadata and noindex. Add an entry in src/seo/pageMeta.js, and add ` +
        `the route to public/sitemap.xml if it should be indexed.`
    );
  }

  // Absolute, self-canonical URL. Trailing slash only on the homepage, matching
  // what the live site serves.
  const canonical =
    routePath === "/" ? `${SITE_URL}/` : `${SITE_URL}${routePath}`;

  return (
    <>
      {/* Unconditional: both values fall back to brand defaults, so they are
          always present. A missing title here means a blank browser tab. */}
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonical} />
      {resolvedNoindex && <meta name="robots" content="noindex, follow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Takamori Robot" />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:alt" content={resolvedImageAlt} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />
      <meta name="twitter:image:alt" content={resolvedImageAlt} />

      {/*
        Route-specific JSON-LD. Rendered in place rather than hoisted — Google
        reads JSON-LD from anywhere in the document, so this is fine.
        dangerouslySetInnerHTML is safe here: the content is JSON.stringify of
        our own static objects in src/seo/pageMeta.js, never user input.
      */}
      {(PAGE_JSON_LD[routePath] ?? []).map((schema, i) => (
        <script
          key={`${routePath}-ld-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}

      {/* Extra JSON-LD or tags passed in by a caller */}
      {children}
    </>
  );
}
